import { AxiosResponse } from 'axios';
import { httpApi } from 'api/http.api';
import {
  CreateIncidenceRequestPayload,
  IncidenceRequestDetailRecord,
  IncidenceRequestRhVisibleApiStatus,
  IncidenceRequestsPendingSummaryResponse,
  IncidenceRequestRecord,
  IncidenceRequestsListResponse,
  PresignIncidenceRequestAttachmentPayload,
  PresignIncidenceRequestAttachmentDownloadPayload,
  PresignIncidenceRequestAttachmentDownloadResponse,
  PresignIncidenceRequestAttachmentResponse,
  ReassignIncidenceRequestSupervisorPayload,
  UpdateIncidenceRequestStatusPayload,
  UpdateIncidenceRequestPayload,
} from 'types/incidenceRequests';

const controller = 'incidences/requests';

export const listMyIncidenceRequests = ({
  page = 1,
  limit = 10,
  incidenceCategory,
  status,
  startDate,
  endDate,
}: {
  page?: number;
  limit?: number;
  incidenceCategory?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}): Promise<AxiosResponse<IncidenceRequestsListResponse>> =>
  httpApi
    .get<IncidenceRequestsListResponse>(`${controller}/mine`, {
      params: {
        page,
        limit,
        ...(incidenceCategory ? { incidenceCategory } : {}),
        ...(status ? { status } : {}),
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      },
    })
    .then((res) => res);

export const listPendingApprovalIncidenceRequests = ({
  page = 1,
  limit = 10,
  folio,
  requester,
  incidenceCategory,
  status,
  rhVisibleStatus,
  startDate,
  endDate,
}: {
  page?: number;
  limit?: number;
  folio?: string;
  requester?: string;
  incidenceCategory?: string;
  status?: string;
  rhVisibleStatus?: IncidenceRequestRhVisibleApiStatus;
  startDate?: string;
  endDate?: string;
}): Promise<AxiosResponse<IncidenceRequestsListResponse>> =>
  httpApi
    .get<IncidenceRequestsListResponse>(`${controller}/pending-approval`, {
      params: {
        page,
        limit,
        ...(folio ? { folio } : {}),
        ...(requester ? { requester } : {}),
        ...(incidenceCategory ? { incidenceCategory } : {}),
        ...(status ? { status } : {}),
        ...(rhVisibleStatus ? { rhVisibleStatus } : {}),
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      },
    })
    .then((res) => res);

export const getPendingApprovalIncidenceRequestsSummary = (): Promise<
  AxiosResponse<IncidenceRequestsPendingSummaryResponse>
> =>
  httpApi
    .get<IncidenceRequestsPendingSummaryResponse>(
      `${controller}/pending-approval/summary`
    )
    .then((res) => res);

export const createIncidenceRequest = (
  payload: CreateIncidenceRequestPayload
): Promise<AxiosResponse<IncidenceRequestRecord>> =>
  httpApi.post<IncidenceRequestRecord>(controller, payload).then((res) => res);

export const updateIncidenceRequest = (
  id: string,
  payload: UpdateIncidenceRequestPayload
): Promise<AxiosResponse<IncidenceRequestRecord>> =>
  httpApi
    .put<IncidenceRequestRecord>(`${controller}/${id}`, payload)
    .then((res) => res);

export const deleteIncidenceRequest = (
  id: string
): Promise<AxiosResponse<{ message: string }>> =>
  httpApi.delete<{ message: string }>(`${controller}/${id}`).then((res) => res);

export const getIncidenceRequestDetail = (
  id: string
): Promise<AxiosResponse<IncidenceRequestDetailRecord>> =>
  httpApi
    .get<IncidenceRequestDetailRecord>(`${controller}/${id}`)
    .then((res) => res);

export const updateIncidenceRequestStatus = (
  id: string,
  payload: UpdateIncidenceRequestStatusPayload
): Promise<AxiosResponse<IncidenceRequestRecord>> =>
  httpApi
    .patch<IncidenceRequestRecord>(`${controller}/${id}/status`, payload)
    .then((res) => res);

export const reassignIncidenceRequestSupervisor = (
  id: string,
  payload: ReassignIncidenceRequestSupervisorPayload
): Promise<AxiosResponse<IncidenceRequestDetailRecord>> =>
  httpApi
    .patch<IncidenceRequestDetailRecord>(
      `${controller}/${id}/supervisor`,
      payload
    )
    .then((res) => res);

export const presignIncidenceRequestAttachment = (
  payload: PresignIncidenceRequestAttachmentPayload
): Promise<AxiosResponse<PresignIncidenceRequestAttachmentResponse>> =>
  httpApi
    .post<PresignIncidenceRequestAttachmentResponse>(
      `${controller}/attachments/presign`,
      payload
    )
    .then((res) => res);

export const presignIncidenceRequestAttachmentDownload = (
  id: string,
  payload: PresignIncidenceRequestAttachmentDownloadPayload
): Promise<AxiosResponse<PresignIncidenceRequestAttachmentDownloadResponse>> =>
  httpApi
    .post<PresignIncidenceRequestAttachmentDownloadResponse>(
      `${controller}/${id}/attachments/download-url`,
      payload
    )
    .then((res) => res);
